import React from 'react'
import PropTypes from 'prop-types'
import _ from 'underscore'
import JoiBrowser from 'joi-browser'
import { HiArrowUturnDown, HiPrinter } from 'react-icons/hi2'
import { useLazyQuery, useQuery } from '@apollo/client'
import { DataGrid } from '@mui/x-data-grid'
import { toast } from 'react-hot-toast'
import { connect } from 'react-redux'

import {
  Box,
  Button,
  Flex,
  Text,
  useBreakpointValue,
  useDisclosure
} from '@chakra-ui/react'

import DateRangePicker from 'components/DateRangePicker'
import TableSpinner from 'components/TableSpinner'
import { MemoizedSelect } from 'components/MemoizedInput'
import Modal from 'components/Modal'
import EmailReportModal from 'components/EmailReport'
import CustomerAccountSelector from 'components/CustomerAccountSelector'
import VendorAccountSelector from 'components/VendorAccountSelector'

import SmsReadLatency from './__query__/index.latency.sms.read.query'
import MccReadQuery from 'components/MccMncSelector/__query__/index.mcc.read.query'

const latencyRanges = [
  { label: "0-10 secs", min: 0, max: 10 },
  { label: "10-30 secs", min: 10, max: 30 },
  { label: "30-60 secs", min: 30, max: 60 },
  { label: "1-2 mins", min: 60, max: 120 },
  { label: "2-3 mins", min: 120, max: 180 },
  { label: "3-4 mins", min: 180, max: 240 },
  { label: "4-5 mins", min: 240, max: 300 },
  { label: "5-60 mins", min: 300, max: 3600 },
  { label: "1-24 hrs", min: 3600, max: 86400 }
]

const _JoiSchema = JoiBrowser.object({
  'customerAccountId': JoiBrowser.string().required().trim()
}).options({ 'allowUnknown': true })

const Index = () => {
  const [dateRange, setDateRange] = React.useState({
    'selection': {
      'startDate': Object.DateRangeProvider()?.startDate,
      'endDate': Object.DateRangeProvider()?.endDate,
      'key': 'selection'
    }
  })
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })

  const [customerAccountId, setCustomerAccountId] = React.useState('')
  const [vendorNameToSearch, setVendorNameToSearch] = React.useState('')
  const [countryNameToSearch, setCountryNameToSearch] = React.useState('')

  const { isOpen: isEmailReportModalOpen, onOpen: onEmailReportModalOpen, onClose: onEmailReportModalClose } = useDisclosure()

  // Ref assignment.
  const _formDataRef = React.useRef({
    customerAccountId: '',
    vendorNameToSearch: '',
    countryNameToSearch: ''
  })
  // Hook assignment.
  const [QuerySmsReadLatency, QuerySmsReadLatencyResponse] = useLazyQuery(SmsReadLatency)
  const _QueryMccRead = useQuery(MccReadQuery, { 'variables': { 'take': 300, 'skip': 0 }, 'fetchPolicy': Object.React.App.fetchPolicy, 'pollInterval': 0 })

  const _isFirstLoadCompleted = React.useRef(false)
  const _tableHeaderHeightRef = React.useRef(0)
  const _isCurrentViewMobile = useBreakpointValue({
    'base': 'false',
    'md': false,
    'lg': false,
    'xl': false,
    'sm': true,
    'xs': true
  })
  // Handler functions.
  const _onCustomerAccountInputChange = React.useRef(_.debounce((e) => {
    _formDataRef.current.customerAccountId = e.target.value
    setCustomerAccountId(e.target.value)
  }, 800)).current

  const _onVendorNameInputChange = React.useRef(_.debounce((e) => {
    _formDataRef.current.vendorNameToSearch = e.target.value
    setVendorNameToSearch(e.target.value)
  }, 800)).current

  const _handleDateRangeChange = (ranges) => {
    setDateRange(ranges)
  }

  const _handleCustomerAccountChange = (e) => {
    setCustomerAccountId(e.target.value)
  }

  const _handleVendorAccountChange = (e) => {
    setVendorNameToSearch(e.target.value)
  }

  const _handleCountryNameChange = (e) => {
    setCountryNameToSearch(e.target.value)
    _formDataRef.current.countryNameToSearch = e.target.value
  }

  // Object assignment.
  const _handleSubmit = () => {
    const { error } = _JoiSchema.validate({
      'customerAccountId': customerAccountId
    })

    if (error) {
      toast.error(error.details[0]?.message || 'Validation failed')
      return
    }

    QuerySmsReadLatency({
      'variables': {
        'startDate': dateRange?.selection.startDate,
        'endDate': dateRange?.selection.endDate,
        'latencyRanges': latencyRanges,
        'customerAccountId': customerAccountId?.includes('(') ? customerAccountId.split('(')[1].replace(')', '').trim() : customerAccountId,
        'vendorNameToSearch': vendorNameToSearch?.includes('(') ? vendorNameToSearch.split('(')[0].trim() : vendorNameToSearch,
        'countryNameToSearch': countryNameToSearch
      }
    })
  }

  React.useEffect(() => {
    return () => {
      _onCustomerAccountInputChange.cancel?.()
      _onVendorNameInputChange.cancel?.()
    }
  }, [])

  if (!QuerySmsReadLatencyResponse?.loading && QuerySmsReadLatencyResponse?.data?.SmsReadLatency) _isFirstLoadCompleted.current = true

  const columns = React.useMemo(() => [
    {
      'field': 'latencyRange',
      'headerName': 'Latency Range (Success Latency)',
      'flex': 1,
      'minWidth': 250,
      'align': 'center',
      'headerAlign': 'center',
      'editable': false,
      'sortable': true,
    },
    {
      'field': 'count',
      'headerName': 'Count',
      'flex': 1,
      'minWidth': 150,
      'align': 'center',
      'headerAlign': 'center',
      'editable': false,
      'sortable': true,
      'filterable': true,
    },
    {
      'field': 'percentage',
      'headerName': '%age',
      'flex': 1,
      'minWidth': 150,
      'align': 'center',
      'headerAlign': 'center',
      'editable': false,
      'sortable': true,
      'filterable': true,
      'valueFormatter': (value) => `${value?.toFixed?.(2) ?? '0'}%`
    },
  ], [])

  // Data transformation for table rows.
  const rows = React.useMemo(() => {
    if (!QuerySmsReadLatencyResponse?.data?.SmsReadLatency?._reportData) return []

    const flattenedRows = []
    QuerySmsReadLatencyResponse.data.SmsReadLatency._reportData.forEach((reportItem) => {
      if (reportItem.latencyDistribution && Array.isArray(reportItem.latencyDistribution)) {
        reportItem.latencyDistribution.forEach((latencyData, distIndex) => {
          flattenedRows.push({
            'id': `${reportItem.customerAccountId}-${latencyData.range}-${distIndex}`,
            'latencyRange': latencyData.range,
            'count': latencyData.count || 0,
            'percentage': latencyData.percentage || 0
          })
        })
      }
    })
    return flattenedRows
  }, [QuerySmsReadLatencyResponse?.data?.SmsReadLatency?._reportData])

  return (
    <>
      <Flex className='reportsLatency base'>
        <Flex
          display='flex'
          flex={1}
          flexDirection='column'
          gap={_isCurrentViewMobile ? '12px' : '22px'}
          bg='white'
          borderRadius='20px'
          p={_isCurrentViewMobile ? '12px' : '22px'}>
          <Flex pb='0px' justify='space-between' alignItems='center'>
            <Flex flex={0.6} gap='6px' flexDir='column'>
              <Text
                as='h3'
                fontSize={17}
                fontWeight={500}
                display='flex'
                alignItems='center'
                color='brand.500'
                gap='6px'
              >
                <HiArrowUturnDown />
                {_isCurrentViewMobile ? 'Filter Rows' : 'Filter Rows based on Date.'}
              </Text>
              <DateRangePicker
                startDate={dateRange?.selection?.startDate}
                endDate={dateRange?.selection?.endDate}
                w='100%'
                fontSize='13px'
                height='max-content'
                gap='6px'
                setSelectDate={_handleDateRangeChange}
              />
            </Flex>
            <Button
              borderRadius='12px'
              maxW='250px'
              backgroundColor='purple.200'
              color='purple'
              aria-label='Download report.'
              onClick={onEmailReportModalOpen}
              _hover={{ 'bg': 'purple.800', 'color': 'white' }}
            >
              {_isCurrentViewMobile ? void 0 : <Text as='h4' mr={2} fontWeight='500'>Download Sheet</Text>}
              <HiPrinter size='20px' />
            </Button>
          </Flex>

          <Flex
            gap='12px'
            flexWrap='wrap'
            bg='white'
            p='16px'
            borderRadius='12px'
            border='1px solid #C5CFE8'
            alignItems='flex-end'
          >
            <Box flex='1' minW='200px'>
              <CustomerAccountSelector
                w={{ base: '100%', md: '90%' }}
                name='customerAccountId'
                label='Customer Account (SMPP Account)'
                labelColor='rgb(141, 151, 181)'
                value={customerAccountId}
                onChange={_handleCustomerAccountChange}
                isRequired={true}
              />
            </Box>
            <Box flex='1' minW='200px'>
              <Text mb={2} ml={2} fontSize='14px' fontWeight={600} color='rgb(141, 151, 181)'>Vendor Account</Text>
              <VendorAccountSelector
                w={{ base: '100%', md: '90%' }}
                value={vendorNameToSearch}
                onChange={_handleVendorAccountChange}
              />
            </Box>
            <Box flex='1' minW='200px'>
              <Text mb={2} ml={8} fontSize='14px' fontWeight={600} color='rgb(141, 151, 181)'>Country</Text>
              <MemoizedSelect
                w={{ base: '100%', md: '90%' }}
                placeholder='Select Country'
                disabled={_QueryMccRead?.loading}
                value={countryNameToSearch}
                options={_.compact(_QueryMccRead?.data?.MccRead?.map?.(j => 'READ_SUCCESSFUL' === j.status ? j.countryName : void 0))?.sort()}
                onChange={_handleCountryNameChange}
              />
            </Box>
            <Box flex='1' minW='150px'>
              <Button
                width='100%'
                colorScheme='blue'
                onClick={_handleSubmit}
                isLoading={QuerySmsReadLatencyResponse?.loading}
              >
                Check Latency
              </Button>
            </Box>
          </Flex>
          <Box
            position="sticky"
            top="80px"
            width="100%"
            height="calc(100vh - 250px)"
            sx={{
              '& .MuiDataGrid-root': {
                border: '1px solid #E2E8F0',
                borderRadius: '12px',
                height: '100%',
              },
              '& .MuiDataGrid-main': {
                overflowY: 'auto'
              }
            }}
          >
            <DataGrid
              rows={rows}
              columns={columns}
              rowHeight={55}
              slots={{
                'loadingOverlay': () => <TableSpinner isLoading={true} isEmpty={false} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
                'noResultsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
                'noRowsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
                'footer': () => (
                  <Box p={2} pr={10} minH={50} display='flex' alignItems='center' justifyContent='flex-end' fontWeight={500}>
                    Total Count: {QuerySmsReadLatencyResponse?.data?.SmsReadLatency?._reportData?.reduce((sum, item) => sum + (item.totalSmsCount || 0), 0) || 0}
                  </Box>
                )
              }}
              disableRowSelectionOnClick
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
              loading={QuerySmsReadLatencyResponse?.loading}
            />
          </Box>
        </Flex>

        {/* Download Report Modal - displays when user clicks the Download Sheet button */}
        <Modal
          size='md'
          title='Download Report'
          isOpen={isEmailReportModalOpen}
          isCentered={true}
          onClose={onEmailReportModalClose}
        >
          {/* Email Report form component with submit handler */}
          <EmailReportModal
            onSubmit={() => {
              // Show notification that download feature is under development
              toast('Download feature coming soon')
              // Close the modal after submission
              onEmailReportModalClose()
            }}
            isLoading={false}
          />
        </Modal>
      </Flex>
    </>
  )
}

Index.propTypes = {
  'vendorId': PropTypes.object.isRequired,
  'passOn': PropTypes.object,
  'dispatch': PropTypes.func.isRequired,

}

const _MapStateToProps = __state => ({
  'passOn': __state.PassOn,
})

export default connect(_MapStateToProps)(Index)
